import { useQueryData } from "@src/hooks/useQueryData";
import { byCode, reloadAndReplaceCachedListItem } from "@src/hooks/queryListCache";
import {
    onsideInvoicesServices,
    type OnsideInvoice,
    type OnsideInvoiceContent,
    type OnsideInvoiceContentExtends,
    type OnsideInvoiceExtends,
} from "../../../../api/onsideInvoices";

/** Связи карточки НС для отгрузки СГП. */
const FINISHED_GOODS_SHIPMENT_CARD_INCLUDES = {
    include: ["WsSender", "Enterprise", "State"] satisfies Array<keyof OnsideInvoiceExtends>,
} as const;

/** Связи строк НС для отгрузки СГП. */
const FINISHED_GOODS_SHIPMENT_CONTENT_INCLUDES = {
    include: ["StockObj", "Measure", "Lot", "Order", "DepotCard", "SpMovings"] satisfies Array<
        keyof OnsideInvoiceContentExtends
    >,
} as const;

/** Загружает карточку НС для отгрузки СГП. */
export function useStockFinishedGoodsShipmentCardQuery(invoiceCode: number) {
    return useQueryData<OnsideInvoice | null>({
        queryKey: ["stock", "finishedGoodsShipment", invoiceCode, "card"],
        queryFn: ({ signal }) =>
            onsideInvoicesServices.loadByCode(
                {
                    code: invoiceCode,
                    ...FINISHED_GOODS_SHIPMENT_CARD_INCLUDES,
                },
                { signal },
            ),
        enabled: invoiceCode > 0,
        meta: { errorSource: "stock.finishedGoodsShipment.card" },
    });
}

/** Результат загрузки строк НС для отгрузки СГП. */
export interface UseStockFinishedGoodsShipmentContentsQueryResult {
    /** Строки НС. */
    content: OnsideInvoiceContent[];
    /** Признак фоновой загрузки. */
    isFetching: boolean;
    /** Перезагружает строки НС. */
    refetch: () => Promise<unknown>;
    /**
     * Перезагружает одну строку НС и заменяет её в query cache.
     * @param contentCode код строки состава НС
     */
    refetchContentByCode: (contentCode: number) => Promise<void>;
}

/** Загружает строки НС для отгрузки СГП. */
export function useStockFinishedGoodsShipmentContentsQuery(
    invoiceCode: number,
): UseStockFinishedGoodsShipmentContentsQueryResult {
    const query = useQueryData<OnsideInvoiceContent[]>({
        queryKey: ["stock", "finishedGoodsShipment", invoiceCode, "contents"],
        queryFn: ({ signal }) =>
            onsideInvoicesServices.content.load(
                {
                    filter: { InvoiceCodes: [invoiceCode] },
                    ...FINISHED_GOODS_SHIPMENT_CONTENT_INCLUDES,
                },
                { signal },
            ),
        enabled: invoiceCode > 0,
        meta: { errorSource: "stock.finishedGoodsShipment.contents" },
    });

    const refetchContentByCode = async (contentCode: number): Promise<void> => {
        await reloadAndReplaceCachedListItem({
            setData: query.setData,
            getKey: byCode,
            loadItem: async () => {
                const items = await onsideInvoicesServices.content.load({
                    filter: { Codes: [contentCode], InvoiceCodes: [invoiceCode] },
                    ...FINISHED_GOODS_SHIPMENT_CONTENT_INCLUDES,
                });
                return items[0] ?? null;
            },
        });
    };

    return {
        content: query.data ?? [],
        isFetching: query.isFetching,
        refetch: query.refetch,
        refetchContentByCode,
    };
}
